import React from 'react';
const { useState, useEffect } = React;
import { fmtInTz } from '../coachingTime.js';
import { COACHING_FOCUS, ASSIGNMENT_KINDS, MIN_GROUP_FOR_AGGREGATES, label } from '../coachingConfig.js';
import { ProgressRing, MetricBar, MilestonePath } from './JourneyViews.jsx';
import { METRIC_COUNTS } from '../journey.js';

const fmtDay = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00Z`).toLocaleDateString('es', { dateStyle: 'medium' }) : '');
function TestBand({ environment }) {
  if (environment !== 'test') return null;
  return <div className="msg" style={{ background: 'rgba(251,191,36,0.15)', color: 'var(--warn)' }}>Entorno de prueba: no es producción.</div>;
}

/**
 * Whoever hires or leads a team (their personal space, or an earlier read-only link): only the group. Program and
 * shared goals, the progress of the group sessions (the same rules as Mi recorrido), coming sessions, the team's
 * activities and materials with their shared instructions, milestones and the team's next step. Never anything
 * individual: messages, notes, reflections, individual goals, submissions or feedback. Individual sessions only as
 * the team's total and only with enough people (MIN_GROUP_FOR_AGGREGATES). `preview`: the coach's «Ver como» (read only).
 */
export default function SponsorView({ load, onBack = null, preview = false, backLabel = '← Mi espacio' }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  // The earlier read-only link, the person's own space or the coach's preview: the caller says how to read it
  useEffect(() => { load().then(setData).catch(e => setError(e.message)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const backRow = onBack && <div className="row" style={{ margin: '2px 0 10px' }}><button className="btn small" onClick={onBack}>{backLabel}</button></div>;
  if (error || !data) return <>{backRow}{error ? <div className="card msg err">{error}</div> : <div className="card">Cargando…</div>}</>;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const pr = data.progress || {};
  const statusWord = { unscheduled: 'Sin fecha', scheduled: 'Agendada', done: 'Realizada', cancelled: 'Cancelada' };
  return (
    <>
      <TestBand environment={data.environment} />
      {backRow}
      <h1>{data.process.name}</h1>
      <p className="muted">Vista de quien contrata o lidera el equipo{data.teamSize ? ` · equipo de ${data.teamSize} ${data.teamSize === 1 ? 'persona' : 'personas'}` : ''}. Solo información grupal: lo individual de cada persona queda entre esa persona y la coach.</p>

      <section className="jr-hero" aria-labelledby="sp-next">
        <div className="lrn-eyebrow" id="sp-next">Próximo paso del equipo</div>
        {data.next ? <>
          <h2 className="jr-hero-title">{data.next.title}</h2>
          <p className="jr-hero-sub">{data.next.kind === 'activity' ? `Actividad del equipo · para el ${fmtDay(data.next.when)}` : fmtInTz(data.next.when, tz)}</p>
        </> : <p style={{ margin: '6px 0 0' }}>{data.process.status === 'closed' ? 'El proceso terminó.' : 'Sin sesiones grupales agendadas por ahora.'}</p>}
      </section>

      <div className="card">
        <h2>Progreso del equipo</h2>
        <div className="jr-card-head">
          {pr.sessions ? <ProgressRing metric={pr.sessions} label={false} /> : null}
          <div style={{ minWidth: 0 }}>
            <p className="jr-primary" style={{ margin: 0 }}>{pr.sessions ? pr.sessions.label : 'Las sesiones grupales se definen al empezar.'}</p>
            {pr.sessions && <p className="muted" style={{ margin: '2px 0 0' }}>{METRIC_COUNTS.groupSessions}</p>}
          </div>
        </div>
        {pr.goals && <><MetricBar metric={pr.goals} title="Objetivos compartidos" /><p className="muted" style={{ margin: '2px 0 0' }}>{METRIC_COUNTS.sharedGoals}</p></>}
        {pr.milestones?.length > 0 && (
          <details className="jr-details">
            <summary>Hitos del equipo ({pr.milestones.filter(x => x.state === 'done').length} de {pr.milestones.length})</summary>
            <MilestonePath steps={pr.milestones} tz={tz} title="Hitos del equipo" />
          </details>
        )}
        <p className="muted" style={{ marginTop: 10 }}>
          {data.individualSessions.hidden
            ? `Con menos de ${MIN_GROUP_FOR_AGGREGATES} personas no se muestra ningún dato de las sesiones individuales.`
            : `Sesiones individuales del equipo: ${data.individualSessions.done} realizadas de ${data.individualSessions.total}, en total (nunca por persona).`}
        </p>
      </div>

      <div className="card">
        <h2>Programa y objetivos compartidos</h2>
        <p>Foco {data.process.focusAgreed ? 'acordado' : 'propuesto'}: <b>{label(COACHING_FOCUS, data.process.focus)}</b></p>
        {data.process.sharedGoals.length
          ? <ul className="plain">{data.process.sharedGoals.map(g => <li key={g.id}>{g.status === 'achieved' ? '✓ ' : ''}{g.text}{g.status === 'achieved' ? <span className="muted"> · logrado</span> : ''}</li>)}</ul>
          : <p className="muted">Los objetivos compartidos se acuerdan en la primera sesión grupal.</p>}
        <h3 style={{ marginTop: 14 }}>Sesiones grupales</h3>
        {data.groupSessions.map(s => (
          <div key={`${s.number}-${s.title}`} className="item">
            <b>{s.title}</b> <span className={`badge ${s.status === 'done' ? 'on' : ''}`}>{statusWord[s.status]}</span>
            {s.startsAt && <div className="muted">{fmtInTz(s.startsAt, tz)}</div>}
          </div>
        ))}
      </div>

      {data.upcomingGroupSessions?.length > 0 && (
        <div className="card"><h2>Próximas sesiones grupales</h2>{data.upcomingGroupSessions.map(s => <div key={s.id || s.number} className="item"><b>{s.title || `Sesión ${s.number || ''}`}</b><div className="muted">{fmtInTz(s.startsAt, tz)}</div></div>)}</div>
      )}

      {data.groupActivities.length > 0 && (
        <div className="card">
          <h2>Actividades y materiales del equipo</h2>
          {data.groupActivities.map((a, i) => (
            <div key={i} className="item">
              <b>{a.title}</b> <span className="badge">{label(ASSIGNMENT_KINDS, a.kind)}</span>{a.dueOn && <span className="muted"> · para el {fmtDay(a.dueOn)}</span>}
              {a.instructions && <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{a.instructions}</div>}
              {a.link && /^https?:\/\//.test(a.link) && <div style={{ marginTop: 4 }}><a href={a.link} target="_blank" rel="noopener noreferrer">Abrir el material</a></div>}
            </div>
          ))}
          <p className="muted" style={{ marginTop: 8 }}>Solo las actividades del equipo, con lo que todo el equipo ve. Nunca las individuales, las respuestas, entregas ni devoluciones de nadie.</p>
        </div>
      )}
      {preview && <p className="muted">Vista previa de solo lectura: así lo ve con su link personal.</p>}
    </>
  );
}
